import { addDays, weekdayOf, type ISODate } from '@app/shared'
import type { CaptureType } from '../../lib/data'

// ============================================================================
// TYPE DETECTION — LifeOS Chat.dc.html, option 11c.
//
// README §11: "**Type detection is automatic and shown, not asked.**" and
// §Interactions: "Type detection runs on input (URL → clip with fetched preview,
// date/time phrase → reminder, imperative phrase → task); the detected type is
// preselected and always overridable. Unset type files to Inbox."
//
// THIS IS A HEURISTIC AND IT IS LABELLED ONE — here, and in the UI, which shows
// the guess as a preselected chip you can change rather than asking a question or
// silently filing. There is no model behind it and there is not meant to be: a
// palette that took 400ms to decide what you meant would be a palette you stop
// using. It is three ordered rules over a string.
//
// THE ORDER IS THE README'S, not a preference: URL, then date phrase, then
// imperative. It matters for "email Maya tomorrow", which the README's order
// makes a reminder rather than a dated task. Deliberate: a dated thing you want
// to be nudged about is what the phone's reminders app was for, and this palette
// is replacing that.
//
// NOTHING HERE READS THE CLOCK. `today` is passed in from the app's own clock, so
// demo mode never drifts and 'tomorrow' resolves the same in every screenshot.
// ============================================================================

/** The one definition of a link in this app; demo.ts holds the matching one for saving. */
export const looksLikeUrl = (text: string) =>
  /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(text.trim())

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

/**
 * Verbs that start a sentence someone is telling themself to do. Deliberately short and
 * deliberately literal — a longer list starts catching "read Clark & Chalmers again", which is a
 * note about a thing, not a task. The chip is overridable precisely because this line is fuzzy.
 */
const IMPERATIVES = new Set([
  'add', 'apply', 'ask', 'book', 'bring', 'buy', 'call', 'cancel', 'check', 'clean', 'confirm',
  'draft', 'email', 'file', 'find', 'finish', 'fix', 'mail', 'message', 'move', 'order', 'pay',
  'pick', 'print', 'register', 'renew', 'reply', 'return', 'schedule', 'send', 'sign', 'submit',
  'text', 'upload', 'write',
])

export interface Detection {
  /** Null means nothing matched, which is a real answer: an unset capture files to the Inbox. */
  type: CaptureType | null
  /** The date the phrase resolved to, when one did. Never guessed for a type with no phrase. */
  due: ISODate | null
  /** The phrase that decided it, for the UI to be able to say WHY — shown, not asked. */
  because: string | null
}

const none: Detection = { type: null, due: null, because: null }

/** The next occurrence of a weekday strictly after `today`; `next` pushes it a further week. */
function nextWeekday(today: ISODate, target: number, next: boolean): ISODate {
  let delta = (target - weekdayOf(today) + 7) % 7
  if (delta === 0) delta = 7
  return addDays(today, next ? delta + 7 : delta)
}

export function detect(text: string, today: ISODate | undefined): Detection {
  const trimmed = text.trim()
  if (!trimmed) return none

  // 1 · A LINK. Checked first because a URL containing the word "monday" is still a link.
  if (looksLikeUrl(trimmed)) return { type: 'clip', due: null, because: 'link detected' }

  const lower = trimmed.toLowerCase()

  // 2 · A DATE OR TIME PHRASE. Without a clock there is nothing to resolve a phrase against, so
  // the rule is skipped entirely rather than resolved against a guess.
  if (today) {
    if (/\btonight\b|\btoday\b/.test(lower)) {
      return { type: 'reminder', due: today, because: 'a time today' }
    }
    if (/\btomorrow\b/.test(lower)) {
      return { type: 'reminder', due: addDays(today, 1), because: 'tomorrow' }
    }
    if (/\bnext week\b/.test(lower)) {
      return { type: 'reminder', due: addDays(today, 7), because: 'next week' }
    }
    const day = /\b(next\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/.exec(lower)
    if (day) {
      const target = WEEKDAYS.indexOf(day[2]!)
      return {
        type: 'reminder',
        due: nextWeekday(today, target, Boolean(day[1])),
        because: day[0].trim(),
      }
    }
    // A bare clock time ('at 3pm', '9:30am'). It fixes a time of day but not a day, so the day is
    // today and nothing pretends otherwise.
    const clock = /\b(at\s+)?\d{1,2}(:\d{2})?\s?(am|pm)\b/.exec(lower)
    if (clock) return { type: 'reminder', due: today, because: clock[0].trim() }
  }

  // 3 · AN IMPERATIVE OPENING.
  const first = lower.split(/[^a-z]+/)[0] ?? ''
  if (IMPERATIVES.has(first)) return { type: 'task', due: null, because: `starts with “${first}”` }

  return none
}

/** 'Task' / 'Note' / 'Clip' / 'Reminder' — the chip row, in the mockup's order. */
export const CAPTURE_TYPES: CaptureType[] = ['task', 'note', 'clip', 'reminder']

export const captureTypeLabel = (t: CaptureType) => t.charAt(0).toUpperCase() + t.slice(1)
